import React from 'react';
import { LucideIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ModalHeaderProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  onClose?: () => void;
  className?: string;
  children?: React.ReactNode;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  description,
  icon: Icon,
  badge,
  actions,
  onClose,
  className,
  children,
}) => {
  return (
    <div
      className={cn(
        'flex-none px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-border/50 relative',
        className
      )}
    >
      {/* Header actions */}
      <div className="absolute right-3 sm:right-4 top-3 sm:top-4 z-10 flex items-center gap-2">
        {actions}
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="rounded-full w-8 h-8 p-0 hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Header content */}
      <div className="pr-10 sm:pr-12">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className="flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-base sm:text-lg font-semibold leading-tight truncate">
                {title}
              </h2>
              {badge}
            </div>
            {description && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 sm:mt-1 line-clamp-2">
                {description}
              </p>
            )}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};
